// Seller, chaves de API (`spec/24-croqui-alinhamento.md` §5, WEB-56). Igual ao frame
// `screens/seller/chaves-api.tsx` do croqui em 1440. Zenith (WEB-107).
//
// Foco da tela: a **tabela de chaves**. Quem tem `useQuery`/`useMutation` é esta rota; os
// componentes de `features/api-keys` recebem dado por prop.
//
// Os três frames exportados do croqui (`NovaChave`, `SegredoExibido`, `RevogarChave`) são
// gavetas de AÇÃO, não detalhe de recurso: ficam em estado local, sem URL (`spec/24` §1
// decisão 5 fala de `$id`, que esta tela não tem).
//
// O toggle de modo mora no Shell (WEB-116): esta tela deixou de montar o seu próprio
// `Segmented` de Modo no `PageHeader`, mas continua filtrando a lista pelo `mode` global
// (`useMode()`), que é o mesmo dado.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  API_KEYS_QUERY_KEY,
  type ApiKey,
  apiKeysQueryOptions,
  createApiKey,
  revokeApiKey,
  rotateApiKey,
} from '../api/queries/api-keys.ts';
import { profileQueryOptions } from '../api/queries/overview.ts';
import { useMode } from '../app/mode.tsx';
import { Notice } from '../components/app/notice.tsx';
import { PageHeader } from '../components/app/page-header.tsx';
import { Button } from '../components/ui/button.tsx';
import { type ApiKeyRowError, ApiKeyTable } from '../features/api-keys/api-key-table.tsx';
import {
  activeCount,
  isStepUpCancelled,
  keyErrorMessage,
  keysOfMode,
  MAX_ACTIVE_KEYS_PER_MODE,
} from '../features/api-keys/model.ts';
import { NewKeyDrawer } from '../features/api-keys/new-key-drawer.tsx';
import { type RevealedKey, RevealKeyDrawer } from '../features/api-keys/reveal-key-drawer.tsx';
import { RevokeKeyDrawer } from '../features/api-keys/revoke-key-drawer.tsx';

function ApiKeys() {
  const { mode } = useMode();
  const queryClient = useQueryClient();
  const keys = useQuery(apiKeysQueryOptions());
  // Mesma query do resto do painel (cache compartilhado): o aviso de verificação depende
  // de `kyc_status`, que só existe em `GET /v1/me`.
  const profile = useQuery(profileQueryOptions(mode));

  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState<RevealedKey | undefined>(undefined);
  const [toRevoke, setToRevoke] = useState<ApiKey | undefined>(undefined);
  const [rowError, setRowError] = useState<ApiKeyRowError | undefined>(undefined);
  const [busyKeyId, setBusyKeyId] = useState<string | undefined>(undefined);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [API_KEYS_QUERY_KEY] });
  };

  const create = useMutation({
    // `gcTime: 0` nas duas mutations que devolvem segredo: sem isso o `MutationCache`
    // guardaria `data.key` em claro por mais 5 min depois de a gaveta fechar.
    gcTime: 0,
    mutationFn: async (name: string) => createApiKey({ name, mode }),
    onSuccess: (created) => {
      setCreateOpen(false);
      setRevealed(created);
      void refresh();
    },
  });

  const rotate = useMutation({
    gcTime: 0,
    mutationFn: async (key: ApiKey) => {
      setBusyKeyId(key.id);
      setRowError(undefined);
      return rotateApiKey(key.id);
    },
    onSuccess: (rotated) => {
      setRevealed(rotated);
    },
    onError: (error, key) => {
      if (!isStepUpCancelled(error)) {
        setRowError({ keyId: key.id, message: keyErrorMessage(error, mode) });
      }
    },
    onSettled: async () => {
      setBusyKeyId(undefined);
      await refresh();
    },
  });

  const revoke = useMutation({
    mutationFn: async (key: ApiKey) => {
      setRowError(undefined);
      return revokeApiKey(key.id);
    },
    onSuccess: () => {
      setToRevoke(undefined);
    },
    onError: (error, key) => {
      setToRevoke(undefined);
      if (!isStepUpCancelled(error)) {
        setRowError({ keyId: key.id, message: keyErrorMessage(error, mode) });
      }
    },
    onSettled: refresh,
  });

  /** Fechar a gaveta apaga o segredo do estado E do cache da mutation. */
  const closeReveal = (): void => {
    setRevealed(undefined);
    create.reset();
    rotate.reset();
  };

  const all = keys.data ?? [];
  const rows = keysOfMode(all, mode);
  const active = activeCount(all, mode, new Date());
  const atLimit = active >= MAX_ACTIVE_KEYS_PER_MODE;
  // `undefined` enquanto o perfil carrega: o aviso não pisca antes de a resposta chegar.
  const kycApproved = profile.data === undefined || profile.data.kyc_status === 'approved';

  const openCreate = (): void => {
    create.reset();
    setCreateOpen(true);
  };

  return (
    <>
      <PageHeader
        title="Chaves de API"
        description="Autenticação da integração via header Authorization"
      >
        <Button
          type="button"
          // No teto o botão apaga; quem explica é o rodapé da tabela ("limite atingido ·
          // revogue uma pra criar outra"), como no croqui.
          disabled={atLimit}
          onClick={openCreate}
        >
          Nova chave
        </Button>
      </PageHeader>

      {mode === 'live' && !kycApproved && (
        <Notice
          tone="warning"
          title="Verificação pendente."
          sub="Criar chave em produção exige verificação aprovada; a API recusa com 403 kyc_required."
          action={
            <Button asChild variant="secondary" size="xs">
              <Link to="/app/settings/kyc">Concluir verificação</Link>
            </Button>
          }
        />
      )}

      <ApiKeyTable
        keys={rows}
        mode={mode}
        activeCount={active}
        isLoading={keys.isPending}
        // Erro só quando não há lista: com `retry: false` e o polling de 30 s, um flake
        // num refetch de fundo trocaria a lista carregada por uma tela de falha.
        error={keys.data === undefined ? keys.error : undefined}
        onRetry={() => {
          void keys.refetch();
        }}
        onRotate={(key) => {
          rotate.mutate(key);
        }}
        onRevoke={setToRevoke}
        onCreate={openCreate}
        {...(busyKeyId === undefined ? {} : { busyKeyId })}
        {...(rowError === undefined ? {} : { rowError })}
      />

      <NewKeyDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode={mode}
        kycApproved={kycApproved}
        isPending={create.isPending}
        error={create.error}
        onSubmit={(name) => {
          create.mutate(name);
        }}
      />

      <RevealKeyDrawer secret={revealed} onClose={closeReveal} />

      <RevokeKeyDrawer
        apiKey={toRevoke}
        onOpenChange={(open) => {
          if (!open) {
            setToRevoke(undefined);
          }
        }}
        isPending={revoke.isPending}
        onConfirm={(key) => {
          revoke.mutate(key);
        }}
      />
    </>
  );
}

export const Route = createFileRoute('/app/developers/api-keys/')({ component: ApiKeys });
